import React, { useState, useEffect } from "react";
import { FiX, FiFileText, FiDownload, FiChevronDown, FiShield, FiCalendar } from "react-icons/fi";
import { documents } from "../../Documents";
import { useLanguage } from "../../context/LanguageContext";

export default function DocumentsModal({ isOpen, onClose, initialTab = "reports" }) {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [openIndex, setOpenIndex] = useState(0);
  const [docList, setDocList] = useState(documents);

  useEffect(() => {
    // Fetch dynamic documents list from external JSON file on server
    const fetchDynamicDocs = async () => {
      try {
        const timestamp = Date.now();
        let res = await fetch(`/docs/documents.json?_t=${timestamp}`);
        if (!res.ok) {
          res = await fetch(`/documents.json?_t=${timestamp}`);
        }
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            setDocList(data);
          }
        }
      } catch (e) {
        // Fallback to bundled data silently
      }
    };

    fetchDynamicDocs();
  }, [isOpen]);

  useEffect(() => {
    setActiveTab(initialTab);
    setOpenIndex(0);
  }, [initialTab, isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const currentDocs = docList.filter((doc) =>
    activeTab === "reports" ? doc.reports === true : doc.reports === false
  );

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const localizeDocTitle = (title) => {
    if (language === "ru") {
      return title
        .replace(/Հաշվետվություն/g, "Отчет")
        .replace(/1-ին եռամսյակ/g, "1-й квартал")
        .replace(/2-րդ եռամսյակ/g, "2-й квартал")
        .replace(/3-րդ եռամսյակ/g, "3-й квартал")
        .replace(/4-րդ եռամսյակ/g, "4-й квартал");
    }
    if (language === "en") {
      return title
        .replace(/Հաշվետվություն/g, "Report")
        .replace(/1-ին եռամսյակ/g, "Quarter 1")
        .replace(/2-րդ եռամսյակ/g, "Quarter 2")
        .replace(/3-րդ եռամսյակ/g, "Quarter 3")
        .replace(/4-րդ եռամսյակ/g, "Quarter 4");
    }
    return title;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      <div className="flex min-h-full items-center justify-center p-4 md:p-6">
        <div className="relative w-full max-w-4xl lg:max-w-5xl bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden transform transition-all z-10 flex flex-col max-h-[88vh]">
          {/* Header */}
          <div className="bg-slate-900 text-white p-6 md:p-8 relative flex-shrink-0">
            <button
              onClick={onClose}
              className="absolute top-5 right-5 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors cursor-pointer"
              aria-label="Փակել"
            >
              <FiX size={22} />
            </button>
            <div className="flex items-center space-x-2 text-brandRed text-xs font-semibold uppercase tracking-wider mb-1.5">
              <FiFileText className="text-sm" />
              <span>{t("docsModal.badge")}</span>
            </div>
            <h3 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-white">
              {t("docsModal.title")}
            </h3>
            <p className="text-slate-400 text-xs md:text-sm mt-1 max-w-2xl">
              {t("docsModal.desc")}
            </p>

            {/* Tab switch */}
            <div className="flex space-x-2 mt-6 bg-slate-800/80 p-1.5 rounded-2xl max-w-md">
              <button
                onClick={() => {
                  setActiveTab("reports");
                  setOpenIndex(0);
                }}
                className={`flex-1 py-2.5 px-4 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center justify-center space-x-2 cursor-pointer ${
                  activeTab === "reports"
                    ? "bg-brandRed text-white shadow-md"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                <FiCalendar size={15} />
                <span>{t("docsModal.tabReports")}</span>
              </button>
              <button
                onClick={() => {
                  setActiveTab("terms");
                  setOpenIndex(0);
                }}
                className={`flex-1 py-2.5 px-4 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center justify-center space-x-2 cursor-pointer ${
                  activeTab === "terms"
                    ? "bg-brandRed text-white shadow-md"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                <FiShield size={15} />
                <span>{t("docsModal.tabTerms")}</span>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 md:p-8 overflow-y-auto space-y-3.5 flex-1 bg-slate-50/30">
            {currentDocs.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-sm">
                {t("docsModal.notFound")}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 items-start">
                {currentDocs.map((doc, index) => {
                  const isOpen = openIndex === index;
                  return (
                    <div
                      key={index}
                      className="border border-slate-200/80 rounded-2xl overflow-hidden shadow-sm bg-white transition-all"
                    >
                      <button
                        onClick={() => toggleAccordion(index)}
                        className="w-full flex items-center justify-between p-4 md:p-5 text-left font-bold text-slate-800 hover:bg-slate-50 transition-colors cursor-pointer"
                      >
                        <span className="text-sm font-extrabold text-slate-800 uppercase tracking-wide">
                          {localizeDocTitle(doc.title)}
                        </span>
                        <FiChevronDown
                          className={`text-slate-400 transition-transform duration-300 ${
                            isOpen ? "transform rotate-180 text-brandRed" : ""
                          }`}
                          size={19}
                        />
                      </button>
                      {isOpen && (
                        <div className="p-4 pt-1 bg-slate-50/50 border-t border-slate-100 space-y-2">
                          {doc.documents.map((file, fileIdx) => (
                            <div
                              key={fileIdx}
                              className="flex items-center justify-between p-3 rounded-xl bg-white hover:bg-red-50/40 border border-slate-100 hover:border-red-100 transition-all"
                            >
                              <span className="text-xs font-semibold text-slate-700 pr-2">
                                {localizeDocTitle(file.title)}
                              </span>
                              <a
                                href={file.file}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center space-x-1.5 text-xs font-bold text-brandRed hover:text-white hover:bg-brandRed bg-red-50 px-3 py-1.5 rounded-lg transition-colors flex-shrink-0"
                              >
                                <FiDownload size={13} />
                                <span>{t("docsModal.downloadPdf")}</span>
                              </a>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
